/**
 * MarketingConsentToggle.js
 * ----------------------------------------------------------------------------
 * ADD-ON component for the customer Inbox. A single toggle letting a
 * logged-in customer opt in/out of marketing communications.
 *
 * Wiring into Inbox.js is 2 lines (see integration guide):
 *   1. import this component
 *   2. render <MarketingConsentToggle /> inside the inbox (e.g. just below
 *      <InboxPushSubscription />)
 *
 * Inbox.js itself is otherwise untouched. The component fetches and updates
 * its own state via /api/customers/marketing-consent, so it needs no props.
 */

import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';

function MarketingConsentToggle({ vendorName }) {
  const [consent, setConsent] = useState(null);   // null = still loading
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load current consent on mount.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get('/api/customers/marketing-consent');
        if (active) setConsent(!!res.data.marketing_consent);
      } catch (err) {
        console.error('Error loading marketing consent:', err);
        if (active) setError('Could not load preference');
      }
    })();
    return () => { active = false; };
  }, []);

  const handleToggle = async () => {
    if (consent === null || saving) return;
    const next = !consent;
    setSaving(true);
    setError(null);
    // Optimistic update.
    setConsent(next);
    try {
      await axios.put('/api/customers/marketing-consent',
        { marketing_consent: next });
    } catch (err) {
      console.error('Error updating marketing consent:', err);
      setConsent(!next);            // revert on failure
      setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mct-card">
      <div className="mct-row">
        <div className="mct-text">
          <span className="mct-title">Marketing emails</span>
          <span className="mct-desc">
              Receive offers and updates from {vendorName || 'this business'} by email.
          </span>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={consent === true}
          className={`mct-switch ${consent ? 'mct-switch--on' : ''}`}
          disabled={consent === null || saving}
          onClick={handleToggle}
        >
          <span className="mct-knob" />
        </button>
      </div>

      {consent === null && !error && (
        <p className="mct-status">Loading...</p>
      )}
      {error && <p className="mct-status mct-status--error">{error}</p>}
      {consent !== null && !error && (
        <p className="mct-status">
          {consent ? 'You are subscribed.' : 'You are unsubscribed.'}
        </p>
      )}

      <style>{`
        .mct-card {
          background: #fff; border: 1px solid #e0e0e0;
          border-radius: 8px; padding: 14px 16px; margin-bottom: 16px;
        }
        .mct-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: 14px;
        }
        .mct-text { display: flex; flex-direction: column; }
        .mct-title { font-weight: 600; color: #333; }
        .mct-desc { font-size: 0.82em; color: #777; margin-top: 2px; }
        .mct-switch {
          position: relative; width: 46px; height: 26px;
          border: none; border-radius: 999px; background: #ccc;
          cursor: pointer; flex-shrink: 0; transition: background 0.2s;
          padding: 0;
        }
        .mct-switch--on { background: #2e7d32; }
        .mct-switch:disabled { opacity: 0.6; cursor: default; }
        .mct-knob {
          position: absolute; top: 3px; left: 3px;
          width: 20px; height: 20px; border-radius: 50%;
          background: #fff; transition: left 0.2s;
        }
        .mct-switch--on .mct-knob { left: 23px; }
        .mct-status {
          margin: 8px 0 0; font-size: 0.8em; color: #888;
        }
        .mct-status--error { color: #c62828; }
      `}</style>
    </div>
  );
}

export default MarketingConsentToggle;